/**
 * types/sync/SyncProvider.ts
 * Abstract sync provider interface and supporting types
 *
 * Defines the contract that all sync providers (local, cloud) must implement
 * for reading, writing, and managing sync files across different storage backends.
 */

/**
 * Sync provider identifier type
 * Represents all supported sync storage providers
 */
export type SyncProviderType = 'local' | 'google-drive' | 'dropbox' | 'onedrive' | 'box';

/**
 * Sync provider constants for validation and type safety
 */
export const SyncProviderEnum = {
  Local: 'local',
  GoogleDrive: 'google-drive',
  Dropbox: 'dropbox',
  OneDrive: 'onedrive',
  Box: 'box'
} as const;

/**
 * File metadata returned when listing sync files
 */
export interface SyncFileInfo {
  /** File name */
  filename: string;

  /** File size in bytes */
  size: number;

  /** Last modified timestamp (ISO 8601) */
  lastModified: string;

  /** Optional checksum for integrity verification */
  checksum?: string;
}

/**
 * Error codes for sync operations
 */
export type SyncErrorCode =
  | 'NOT_CONNECTED'
  | 'PERMISSION_DENIED'
  | 'FILE_NOT_FOUND'
  | 'NETWORK_ERROR'
  | 'QUOTA_EXCEEDED'
  | 'INVALID_FILE'
  | 'CONFLICT_DETECTED'
  | 'SCHEMA_VERSION_MISSING'
  | 'SCHEMA_VERSION_INVALID'
  | 'SCHEMA_VERSION_NEWER'
  | 'SCHEMA_VERSION_MISMATCH'
  | 'UNKNOWN_ERROR';

/**
 * Error codes specific to schema version validation failures.
 * Used to narrow SchemaVersionError.code to only schema-related codes.
 */
export type SchemaVersionErrorCode =
  | 'SCHEMA_VERSION_MISSING'
  | 'SCHEMA_VERSION_INVALID'
  | 'SCHEMA_VERSION_NEWER'
  | 'SCHEMA_VERSION_MISMATCH';

/**
 * Structured error information for sync operations
 */
export interface SyncError {
  /** Error code for programmatic handling */
  code: SyncErrorCode;

  /** Human-readable error message */
  message: string;

  /** Provider that generated the error */
  provider: SyncProviderType;

  /** Additional error details (e.g., stack trace, API response) */
  details?: unknown;
}

/**
 * Structured error for schema version validation failures during import.
 *
 * Provider-agnostic: the same artifact format error occurs regardless of
 * which storage backend (local, Google Drive, etc.) is used.
 *
 * Error codes:
 * - SCHEMA_VERSION_MISSING: artifact lacks schemaVersion field entirely
 * - SCHEMA_VERSION_INVALID: schemaVersion present but not a valid number
 * - SCHEMA_VERSION_NEWER: remote version > local (stale browser tab)
 * - SCHEMA_VERSION_MISMATCH: cross-artifact version inconsistency (baseline vs delta)
 */
export interface SchemaVersionError {
  /** Specific schema version error code */
  code: SchemaVersionErrorCode;

  /** Human-readable error message */
  message: string;

  /** Current app's schema version (SYNC_SCHEMA_VERSION constant) */
  localVersion: number;

  /** Remote artifact's schema version (null if missing or invalid) */
  remoteVersion: number | null;

  /** Type of artifact that failed validation (manifest has no schemaVersion) */
  artifactType: 'baseline' | 'delta';

  /** Filename of the artifact that failed validation */
  artifactName: string;

  /** Baseline schema version (for delta mismatch diagnostics) */
  baselineVersion?: number;
}

/**
 * Generic result wrapper for sync operations
 * Provides consistent success/error handling across all providers
 */
export type SyncResult<T> =
  | { success: true; data: T }
  | { success: false; error: SyncError };

/**
 * Conflict information when local and remote data differ
 */
export interface SyncConflict<T = unknown> {
  /** Local data */
  local: T;

  /** Remote data */
  remote: T;

  /** Local last modified timestamp */
  localTimestamp: string;

  /** Remote last modified timestamp */
  remoteTimestamp: string;

  /** File name where conflict occurred */
  filename: string;
}

/**
 * Optional connection options for provider-specific behavior
 *
 * Provider-specific options:
 * - promptMode: OAuth prompt behavior (cloud providers only)
 *   - 'consent': Force consent screen (default for Google Drive)
 *   - 'select_account': Show account chooser (Google Drive)
 * - forceNewSelection: Force new directory picker (local provider only)
 * - oauthPopup: Pre-opened popup window for OAuth flow (cloud providers only)
 *   Required to avoid browser popup blocking when OAuth is triggered after async work.
 *   Must be opened synchronously in user gesture handler before any async operations.
 *
 * Each provider ignores options that don't apply to it (provider-agnostic design).
 */
export interface ProviderConnectOptions {
  /** OAuth prompt mode for cloud provider account selection (ignored by local provider) */
  promptMode?: 'consent' | 'select_account';
  /** Force new directory selection (local provider only, ignored by cloud providers) */
  forceNewSelection?: boolean;
  /**
   * Pre-opened popup window for OAuth flow (cloud providers only).
   * Must be opened synchronously from user gesture to avoid browser blocking.
   * If not provided, provider will attempt to open popup (may be blocked).
   */
  oauthPopup?: Window | null;
}

/**
 * Abstract sync provider interface
 *
 * All sync providers (local directory, Google Drive, Dropbox, etc.) must
 * implement this interface to enable consistent sync operations across
 * different storage backends.
 *
 * @example
 * ```typescript
 * class LocalDirectorySyncProvider implements SyncProvider {
 *   readonly name = 'local';
 *
 *   async connect(): Promise<boolean> {
 *     // Request directory access via File System Access API
 *     return true;
 *   }
 *
 *   async read(filename: string): Promise<string> {
 *     // Read file from local directory
 *     return fileContents;
 *   }
 *   // ... implement other methods
 * }
 * ```
 */
export interface SyncProvider {
  /** Provider identifier (read-only) */
  readonly name: SyncProviderType;

  /**
   * Whether this provider supports binary file operations
   *
   * Providers that support binary operations must implement all four binary methods:
   * readBinary, writeBinary, deleteBinary, existsBinary
   *
   * Use assertBinarySupport() from @/utils/syncProviderUtils to safely narrow
   * the provider type before calling binary methods.
   */
  readonly supportsBinary?: boolean;

  /**
   * Connect and authenticate with the sync provider
   *
   * For local: Request directory access permissions
   * For cloud: OAuth flow or API key authentication
   *
   * @param options - Optional provider-specific connection options
   * @returns Promise resolving to true if connection successful
   */
  connect(options?: ProviderConnectOptions): Promise<boolean>;

  /**
   * Check if provider is currently connected and authenticated
   *
   * @returns Promise resolving to true if connected
   */
  isConnected(): Promise<boolean>;

  /**
   * Read file contents from storage
   *
   * @param filename - Name of file to read
   * @returns Promise resolving to file contents as string
   * @throws SyncError if file not found or permission denied
   */
  read(filename: string): Promise<string>;

  /**
   * Write file contents to storage
   *
   * Creates new file if it doesn't exist, overwrites if it does.
   *
   * @param filename - Name of file to write
   * @param data - File contents as string
   * @returns Promise resolving when write completes
   * @throws SyncError if permission denied or quota exceeded
   */
  write(filename: string, data: string): Promise<void>;

  /**
   * List all available sync files in storage
   *
   * @returns Promise resolving to array of file metadata
   * @throws SyncError if not connected or permission denied
   */
  list(): Promise<SyncFileInfo[]>;

  /**
   * Delete file from storage
   *
   * @param filename - Name of file to delete
   * @returns Promise resolving when deletion completes
   * @throws SyncError if file not found or permission denied
   */
  delete(filename: string): Promise<void>;

  /**
   * Disconnect from provider and cleanup resources
   *
   * @returns Promise resolving when disconnect completes
   */
  disconnect(): Promise<void>;

  /**
   * Get unique identifier for the connected storage location
   *
   * Returns a stable UUID that identifies the specific directory/location.
   * Used for tracking which directory we're synced to, enabling proper
   * conflict detection when switching between directories.
   *
   * @returns Promise resolving to directory UUID or null if not connected
   */
  getDirectoryId(): Promise<string | null>;

  // ─────────────────────────────────────────────────────────────────────────
  // Binary File Operations (optional - check supportsBinary before use)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Read binary file contents from storage
   *
   * @param filename - Name of file to read
   * @returns Promise resolving to file contents as ArrayBuffer
   * @throws SyncError with FILE_NOT_FOUND if file doesn't exist
   * @throws SyncError with PERMISSION_DENIED if access denied
   * @throws SyncError with NOT_CONNECTED if not connected
   */
  readBinary?(filename: string): Promise<ArrayBuffer>;

  /**
   * Write binary file contents to storage
   *
   * Creates new file if it doesn't exist, overwrites if it does.
   *
   * @param filename - Name of file to write
   * @param data - File contents as ArrayBuffer
   * @param mimeType - Optional MIME type hint (used by Google Drive for metadata)
   * @returns Promise resolving when write completes
   * @throws SyncError with PERMISSION_DENIED if access denied
   * @throws SyncError with QUOTA_EXCEEDED if storage limit reached
   * @throws SyncError with NOT_CONNECTED if not connected
   */
  writeBinary?(filename: string, data: ArrayBuffer, mimeType?: string): Promise<void>;

  /**
   * Delete binary file from storage
   *
   * @param filename - Name of file to delete
   * @returns Promise resolving when deletion completes
   * @throws SyncError with FILE_NOT_FOUND if file doesn't exist
   * @throws SyncError with PERMISSION_DENIED if access denied
   * @throws SyncError with NOT_CONNECTED if not connected
   */
  deleteBinary?(filename: string): Promise<void>;

  /**
   * Check if binary file exists in storage
   *
   * @param filename - Name of file to check
   * @returns Promise resolving to true if file exists, false otherwise
   * @throws SyncError with PERMISSION_DENIED if access denied
   * @throws SyncError with NOT_CONNECTED if not connected
   */
  existsBinary?(filename: string): Promise<boolean>;
}
